import Vue from 'vue';
import Vuex from 'vuex';
import axios from 'axios';
import _ from 'lodash';

import { langList, getLang, injectLanguage } from '@/core/i18n.js';
import enUS from '@/lang/en-US.js';
import fingerprint from '@/utilities/fingerprint.js';
import { sendMessageToSentry } from '@/utilities/sentry.js';
import authStore from '@/modules/auth/store';

import router from '@/core/router';

Vue.use(Vuex);

const store = new Vuex.Store({
  state: {
    vendor: '',
    isInit: false,
    isFpLoaded: false,
    visitorId: '',
    caid: '',
    clientId: '',
    moduleUUID: '',
    loginFrom: '',
    sn: '',
    hashedSN: [],
    tcLogInfo: {},
    tcQueue: [],
    allowLang: Object.keys(langList),
    userLang: document.documentElement.lang,
    lang: enUS,
    isInternalError: false,
    // Mini Browser
    miniBrowserHeader: {
      showCloseBtn: false,
      productName: '',
    },
  },
  getters: {
    curLangLabel(state) {
      return langList[state.userLang];
    },
  },
  mutations: {
    setIsInit(state, isInit) {
      state.isInit = isInit;
    },
    setInternalError(state, isError) {
      state.isInternalError = isError;
    },
    setIsFpLoaded(state, isLoaded) {
      state.isFpLoaded = isLoaded;
    },
    setVisitorId(state, visitorId) {
      state.visitorId = visitorId;
    },
    setCaid(state, caid) {
      state.caid = caid;
    },
    setClientId(state, clientId) {
      state.clientId = clientId;
    },
    setModuleUUID(state, uuid) {
      state.moduleUUID = uuid;
    },
    setLoginFrom(state, loginFrom) {
      state.loginFrom = loginFrom;
    },
    setSn(state, sn) {
      state.sn = sn;
    },
    setHashedSN(state, hashedSN) {
      state.hashedSN = hashedSN;
    },
    setTcLogInfo(state, tcLogInfo) {
      state.tcLogInfo = tcLogInfo;
    },
    pushTcQueue(state, tcLog) {
      state.tcQueue.push(tcLog);
    },
    setTcQueue(state, payload) {
      state.tcQueue = payload;
    },
    setVendor(state, payload) {
      state.vendor = payload;
    },
    setAllowLang(state, allowLang) {
      state.allowLang = allowLang;
    },
    setMiniBrowserHeaderInfo(state, { showCloseBtn, productName }) {
      state.miniBrowserHeader.showCloseBtn = showCloseBtn;
      state.miniBrowserHeader.productName = productName;
    },
    injectLanguage,
  },
  actions: {
    async genVisitorId({ state, commit, dispatch }) {
      if (sessionStorage.getItem('visitor')) {
        const visitor = sessionStorage.getItem('visitor');
        Vue.$cookies.set('visitor', visitor);
        commit('setVisitorId', visitor);
      } else if (!state.isFpLoaded) {
        commit('setIsFpLoaded', true);
        const fp = await fingerprint.load();
        const result = await fp.get();
        sessionStorage.setItem('visitor', result.visitorId);
        Vue.$cookies.set('visitor', result.visitorId);
        commit('setVisitorId', result.visitorId);
        
        if (state.tcQueue.length > 0) {
          state.tcQueue.forEach((tcLog) => {
            tcLog.fingerprint = result.visitorId;
            dispatch('sendTcLog', tcLog);
          });
  
          commit('setTcQueue', []);
        }
      }
    },
    triggerEvent({ state, commit, dispatch }, { eventName, addInfo }) {
      Vue.$gtag.event(`${router.currentRoute.path}, ${eventName}`);

      const visitor = sessionStorage.getItem('visitor') || state.visitorId;

      const isHex = process.env.FINGERPRINT_HEX;
      const currentPath = router.currentRoute.path.replace(/\/$/, '');
      const path = isHex ? charToHex(currentPath) : currentPath;
      const event = isHex ? charToHex(eventName) : eventName;
      const queryParams = router.currentRoute.query;

      let others = {
        timestamp: Date.now(),
      };

      if (state.tcLogInfo) {
        others = {
          ...state.tcLogInfo,
          ...others,
        };
      }

      if (state.sn) {
        others = {
          sn: state.hashedSN,
          ...others,
        };
      }

      if (Object.values(queryParams).length > 0 || addInfo) {
        others = { ...queryParams, ...addInfo, ...others };
      }

      const maskPII = (key, value) => {
        const piiFields = ['first_name', 'last_name', 'email'];
        if (piiFields.includes(key)) return value.replace(/./g, '*');
        else return value;
      };

      const ctx = {
        module_id: 'SSO',
        page: path,
        event: event,
        fingerprint: visitor,
        caid: state.caid,
        sn: state.sn || '',
        guid: '',
        environment: process.env.FINGERPRINT_BOX,
        additional_info: others ? encodeURIComponent(JSON.stringify(others, maskPII)) : '',
        module_uuid: state.moduleUUID || '',
        social_id: '',
      };

      // console.log(ctx);

      if (!visitor) {
        commit('pushTcQueue', ctx);
      } else {
        dispatch('sendTcLog', ctx);
      }
    },
    sendTcLog(context, ctx) {
      // console.log(ctx);
      // console.log(ctx.event)

      const inquiry = _.values(ctx).join(',');
      axios.get('https://dataservice.tmok.tm/tc.png?' + inquiry).catch(() => {});
      // axios.get(
      //   'https://dataservice.tmok.tm/tc.png?' + inquiry,
      //   {
      //     // query URL without using browser cache
      //     headers: {
      //       'Cache-Control': 'no-store',
      //       'Expires': '0',
      //     },
      //   }
      // );
    },
    async initialLanguage({ state, commit }, { mod = '', specificLang = '' }) {
      let lang =
        getLang(specificLang) || localStorage.getItem('lang') || getLang(navigator.language);
      
      if (!state.allowLang.includes(lang)) {
        lang = 'en-US';
      }

      document.documentElement.lang = lang;

      const res = await import(`@/lang/${lang}.js`);
      commit('injectLanguage', { userLang: lang, lang: res.default });

      if (mod) {
        const modRes = await import(`@/modules/${camelToHyphen(mod)}/lang/${lang}.js`);
        commit(`${mod}/injectLanguage`, { userLang: lang, lang: modRes.default });
      }
    },
    async setLanguage({ commit, dispatch }, lang) {
      dispatch('triggerEvent', { eventName: 'SetLanguage' });
      document.documentElement.lang = lang;
      localStorage.setItem('lang', lang);

      const res = await import(`@/lang/${lang}.js`);
      commit('injectLanguage', { userLang: lang, lang: res.default });

      const mod = router.currentRoute.name.split('/')[0];

      if (mod !== 'error') {
        const modRes = await import(`@/modules/${camelToHyphen(mod)}/lang/${lang}.js`);
        commit(`${mod}/injectLanguage`, { userLang: lang, lang: modRes.default });
      }
    },
    sendAdobeData(context, analyticsData) {
      var pageName = location.pathname; // (location.pathname) current path without hostname & query_string
      var pageURL = location.href;      // (location.href)     all current path with query_string
      var referrerURL = document.referrer; // previous page URL

      try {
        window.trackVirtualPageView(pageName, pageURL, analyticsData, referrerURL);
      } catch (e){
        sendMessageToSentry('AdobeDataSendFail', {
          level: 'error',
          extra: [
            {
              name: 'error',
              value: e,
            },
          ],
        });
      }
      
    }
  },
  modules: {
    auth: authStore,
  },
});

export default store;

function charToHex(str) {
  let hex = '';

  for (let i = 0; i < str.length; i += 1) {
    hex += str.charCodeAt(i).toString(16);
  }

  return hex;
}

function camelToHyphen(str) {
  return str.replace(/([A-Z])/g, (match) => '-' + match.toLowerCase());
}
